import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  applyAction, createGame, legalActions, puzzleStepText, validateRuleset,
  type Action, type GameEvent, type GameState,
} from '@ic/engine';
import { api, auth, downloadRecoveryCode, newRecoveryCode, removeRecoveryCode } from '../lib/auth';
import { publishedModes, refreshModes } from '../lib/modes';
import { activeRuleset, useStore } from '../lib/store';
import { RecoveryCodeCard } from '../pages/Login';
import { Board } from './Board';
import { Icon } from './Icons';

/** Recovery code management for the logged-in player. */
export function AccountSection() {
  const a = useStore(auth);
  const [password, setPassword] = useState('');
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [err, setErr] = useState('');
  if (!a.user || a.user.guest) return null;

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      const c = await newRecoveryCode(password);
      setCode(c);
      setPassword('');
      downloadRecoveryCode(a.user!.name, c);
    } catch (x) { setErr((x as Error).message); }
  }

  return (
    <section>
      <h2>Account</h2>
      <div className="section-card stack">
        <div>
          <b>Recovery code</b>
          <p className="muted small">{a.hasRecovery
            ? 'You have a recovery code. Making a new one replaces it.'
            : 'You have no recovery code. Without one, a forgotten password cannot be reset.'}</p>
        </div>
        {code ? <RecoveryCodeCard username={a.user.name} code={code} onDone={() => { setCode(null); setOpen(false); }} doneLabel="Done" /> : open ? (
          <form className="stack" onSubmit={create}>
            <label>Your password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></label>
            {err && <div className="error small">{err}</div>}
            <div className="row gap">
              <button className="btn primary">Create and download</button>
              <button type="button" className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="row gap wrap">
            <button className="btn secondary" onClick={() => setOpen(true)}>{a.hasRecovery ? 'New recovery code' : 'Create recovery code'}</button>
            {a.hasRecovery && <button className="btn ghost" onClick={() => { if (confirm('Remove your recovery code? You will not be able to reset a forgotten password.')) void removeRecoveryCode(); }}>Remove</button>}
          </div>
        )}
      </div>
    </section>
  );
}

/** "Admin" in Settings, only for game admins (chosen in the admin panel at /admin): publish modes and daily puzzles. */
export function AdminSection() {
  const a = useStore(auth);
  if (!a.admin) return null;

  return (
    <section className="admin-area">
      <h2>Admin</h2>
      <p className="muted small">You are an admin: the admin panel shows next to the board in your games, and you can publish modes and daily puzzles. Custom rules from the Rule Editor are playable for you.</p>
      <ModesAdmin />
      <PuzzleAdmin />
    </section>
  );
}

function ModesAdmin() {
  const modes = useStore(publishedModes);
  const rs = useStore(activeRuleset);
  const [name, setName] = useState(rs.name);
  const [msg, setMsg] = useState('');
  useEffect(() => { void refreshModes(); }, []);
  useEffect(() => setName(rs.name), [rs.name]);
  const errors = validateRuleset(rs).filter((i) => i.level === 'error');

  async function publish() {
    setMsg('');
    try {
      await api('/admin/modes', { body: { ruleset: { ...rs, name: name.trim() || rs.name } } });
      await refreshModes();
      setMsg('Published. Everybody can pick it on the Play page now.');
    } catch (x) { setMsg((x as Error).message); }
  }

  return (
    <div className="section-card stack">
      <b>New game mode</b>
      <p className="muted small">Publishes the rules currently open in the <Link to="/editor">Rule Editor</Link> as a mode everybody can play (local, bot, online).</p>
      <div className="admin-row">
        <div className="thumb small-thumb"><Board state={createGame(rs)} orientation="w" /></div>
        <div className="stack grow">
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></label>
          {errors.length > 0 && <div className="error small">{errors[0].message}</div>}
          <button className="btn primary" disabled={!!errors.length} onClick={publish}>Publish mode</button>
          {msg && <span className="small">{msg}</span>}
        </div>
      </div>
      {modes.length > 0 && (
        <ul className="admin-list">
          {modes.map((m) => (
            <li key={m.id}>
              <span className="grow">{m.name} <span className="muted small">{m.board.width}×{m.board.height}</span></span>
              <button className="btn ghost sm" onClick={async () => {
                if (!confirm(`Remove the mode "${m.name}" for everybody?`)) return;
                await api(`/admin/modes/${m.id}`, { method: 'DELETE' }).catch((x) => alert((x as Error).message));
                await refreshModes();
              }}><Icon name="x" size={14} /> Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface DailyPuzzle { id: string; day: string; title: string }

function PuzzleAdmin() {
  const rs = useStore(activeRuleset);
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [title, setTitle] = useState('');
  const [hint, setHint] = useState('');
  const [msg, setMsg] = useState('');
  const [list, setList] = useState<DailyPuzzle[]>([]);
  const start = useMemo(() => createGame(rs), [rs]);
  const [state, setState] = useState<GameState>(start);
  const [steps, setSteps] = useState<string[]>([]);
  const [last, setLast] = useState<{ action: Action; events: GameEvent[]; key: number } | null>(null);
  useEffect(() => { setState(start); setSteps([]); setLast(null); }, [start]);

  const load = () => api<{ puzzles: DailyPuzzle[] }>('/puzzles/daily?all=1').then((r) => setList(r.puzzles)).catch(() => {});
  useEffect(() => { void load(); }, []);

  const won = state.result?.winner === 'w';
  const failed = !!state.result && !won || (!state.result && state.turn !== 'w');
  const play = (a: Action) => {
    const r = applyAction(state, a);
    setSteps([...steps, puzzleStepText(state, a)]);
    setState(r.state);
    setLast({ action: a, events: r.events, key: (last?.key ?? 0) + 1 });
  };
  const reset = () => { setState(start); setSteps([]); setLast(null); };

  async function publish() {
    setMsg('');
    try {
      await api('/admin/puzzles', { body: { day, title, hint, ruleset: rs, solution: steps.join(',') } });
      setMsg(`Published for ${day}.`);
      setTitle(''); setHint(''); reset();
      void load();
    } catch (x) { setMsg((x as Error).message); }
  }

  return (
    <div className="section-card stack">
      <b>New daily puzzle</b>
      <p className="muted small">
        Set up the position in the <Link to="/editor">Rule Editor</Link> (White to move), then play the winning move here.
        It must win within White's turn.
      </p>
      <div className="admin-row puzzle-maker">
        <div className="maker-board">
          <Board state={state} orientation="w" legal={!state.result && state.turn === 'w' ? legalActions(state) : []} interactive={!state.result && state.turn === 'w'}
            onAction={play} lastAction={last?.action} events={last?.events} eventsKey={last?.key} />
        </div>
        <div className="stack grow">
          <div className="small">
            Solution: <span className="mono">{steps.join(', ') || '—'}</span>
            {won && <span className="success"> wins</span>}
            {failed && <span className="error"> does not win, reset and try again</span>}
          </div>
          <button className="btn ghost sm" onClick={reset} disabled={!steps.length}><Icon name="restart" size={14} /> Reset</button>
          <label>Date<input type="date" value={day} min={today} onChange={(e) => setDay(e.target.value)} /></label>
          <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="Daily puzzle" /></label>
          <label>Hint<input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={200} placeholder="Optional" /></label>
          <button className="btn primary" disabled={!won} onClick={publish}>Publish puzzle</button>
          {msg && <span className="small">{msg}</span>}
        </div>
      </div>
      {list.length > 0 && (
        <ul className="admin-list">
          {list.map((p) => (
            <li key={p.id}>
              <span className="mono small">{p.day}</span>
              <span className="grow">{p.title}{p.day > today && <span className="tag">scheduled</span>}</span>
              <button className="btn ghost sm" onClick={async () => {
                if (!confirm(`Delete "${p.title}"?`)) return;
                await api(`/admin/puzzles/${p.id}`, { method: 'DELETE' }).catch((x) => alert((x as Error).message));
                void load();
              }}><Icon name="x" size={14} /> Delete</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

